import type { FC } from 'react'
import { lazy, Suspense } from 'react'
import { Trans } from '@lingui/react/macro'

import { useDelay } from '@jotta/hooks/useDelay'
import { delay } from '@jotta/utils/delay'

import { LoadingOverlay } from '../Layout/LoadingOverlay/LoadingOverlay'

function Fallback() {
  const longDelay = useDelay({
    initialValue: false,
    value: true,
    delayMs: 7_000,
  })
  return (
    <LoadingOverlay
      open
      id="lazyWithRetry"
      position="absolute"
      text={
        longDelay && (
          <Trans>
            This is taking longer than usual. Please wait or{' '}
            <button
              className="btn btn-link"
              onClick={() => window.location.reload()}
            >
              reload the page
            </button>
          </Trans>
        )
      }
    />
  )
}

export function lazyWithRetry<T extends FC<any>>(
  load: Parameters<typeof lazy<T>>[0],
): T {
  const LazyComponent = lazy(async () => {
    let retryInterval = 1000
    for (;;) {
      try {
        return await load()
      } catch (e) {
        await delay(retryInterval)
        retryInterval = Math.min(retryInterval * 2, 12_000)
      }
    }
  })
  return ((props: any) => {
    return (
      <Suspense fallback={<Fallback />}>
        <LazyComponent {...(props as any)} />
      </Suspense>
    )
  }) as T
}
